/**
 * Class for SCORM 2004's cmi.learner_preference object
 */
import { BaseCMI } from "../common/base_cmi";
import { Scorm2004ValidationError } from "../../exceptions/scorm2004_exceptions";
import { check2004ValidFormat, check2004ValidRange } from "./validation";
import {
  scorm2004_constants,
  scorm2004_errors,
  scorm2004_regex,
} from "../../constants";

export class CMILearnerPreference extends BaseCMI {
  private __children = scorm2004_constants.student_preference_children;
  private _audio_level = "1";
  private _language = "";
  private _delivery_speed = "1";
  private _audio_captioning = "0";

  /**
   * Constructor for cmi.learner_preference
   */
  constructor() {
    super("cmi.learner_preference");
  }

  /**
   * Called when the API has been reset
   */
  override reset() {
    this._initialized = false;
  }

  /**
   * Getter for __children
   * @return {string}
   * @private
   */
  get _children(): string {
    return this.__children;
  }

  /**
   * Setter for __children. Just throws an error.
   * @param {string} _children
   * @private
   */
  set _children(_children: string) {
    throw new Scorm2004ValidationError(
      this._cmi_element + "._children",
      scorm2004_errors.READ_ONLY_ELEMENT as number,
    );
  }

  /**
   * Getter for _audio_level
   * @return {string}
   */
  get audio_level(): string {
    return this._audio_level;
  }

  /**
   * Setter for _audio_level
   * @param {string} audio_level
   */
  set audio_level(audio_level: string) {
    if (
      check2004ValidFormat(
        this._cmi_element + ".audio_level",
        audio_level,
        scorm2004_regex.CMIDecimal,
      ) &&
      check2004ValidRange(
        this._cmi_element + ".audio_level",
        audio_level,
        scorm2004_regex.audio_range,
      )
    ) {
      this._audio_level = audio_level;
    }
  }

  /**
   * Getter for _language
   * @return {string}
   */
  get language(): string {
    return this._language;
  }

  /**
   * Setter for _language
   * @param {string} language
   */
  set language(language: string) {
    if (check2004ValidFormat(this._cmi_element + ".language", language, scorm2004_regex.CMILang)) {
      this._language = language;
    }
  }

  /**
   * Getter for _delivery_speed
   * @return {string}
   */
  get delivery_speed(): string {
    return this._delivery_speed;
  }

  /**
   * Setter for _delivery_speed
   * @param {string} delivery_speed
   */
  set delivery_speed(delivery_speed: string) {
    if (
      check2004ValidFormat(
        this._cmi_element + ".delivery_speed",
        delivery_speed,
        scorm2004_regex.CMIDecimal,
      ) &&
      check2004ValidRange(
        this._cmi_element + ".delivery_speed",
        delivery_speed,
        scorm2004_regex.speed_range,
      )
    ) {
      // SCORM 2004 spec requires delivery_speed > 0 (not >= 0)
      if (parseFloat(delivery_speed) === 0) {
        throw new Scorm2004ValidationError(
          this._cmi_element + ".delivery_speed",
          scorm2004_errors.VALUE_OUT_OF_RANGE as number,
        );
      }
      this._delivery_speed = delivery_speed;
    }
  }

  /**
   * Getter for _audio_captioning
   * @return {string}
   */
  get audio_captioning(): string {
    return this._audio_captioning;
  }

  /**
   * Setter for _audio_captioning
   * @param {string} audio_captioning
   */
  set audio_captioning(audio_captioning: string) {
    if (
      check2004ValidFormat(
        this._cmi_element + ".audio_captioning",
        audio_captioning,
        scorm2004_regex.CMISInteger,
      ) &&
      check2004ValidRange(
        this._cmi_element + ".audio_captioning",
        audio_captioning,
        scorm2004_regex.text_range,
      )
    ) {
      this._audio_captioning = audio_captioning;
    }
  }

  /**
   * toJSON for cmi.learner_preference
   *
   * @return {
   *    {
   *      audio_level: string,
   *      language: string,
   *      delivery_speed: string,
   *      audio_captioning: string
   *    }
   *  }
   */
  toJSON(): {
    audio_level: string;
    language: string;
    delivery_speed: string;
    audio_captioning: string;
  } {
    this.jsonString = true;
    const result = {
      audio_level: this.audio_level,
      language: this.language,
      delivery_speed: this.delivery_speed,
      audio_captioning: this.audio_captioning,
    };
    this.jsonString = false;
    return result;
  }
}
